/* eslint-disable import/no-cycle */
import Vue from 'vue';
import { API_URL } from '@/config';

const MODULE_ROUTE = 'logoReview';

/**
 * Turn the relative, signed candidate-image path the API returns into an
 * absolute URL.
 *
 * The API deliberately signs these relatively (path + query only), so the
 * signature does not depend on APP_URL — which is wrong or scheme-mismatched
 * in several environments. The origin has to come from the API base URL the
 * app is already configured with, and the path already includes `/api`, so
 * that suffix is stripped before joining.
 */
export function absoluteApiUrl(path) {
  if (!path) return path;
  if (/^https?:\/\//i.test(path)) return path;

  const origin = API_URL.replace(/\/api\/?$/, '');
  return `${origin}${path.startsWith('/') ? '' : '/'}${path}`;
}

/**
 * Re-throw the error the axios interceptor already built.
 *
 * The older services here do `throw new Error(\`${error}\`)`, which flattens a
 * structured error into a string and loses the server's message, status and
 * validation details. Reviewing logos needs the real reason surfaced — a bare
 * "could not accept" tells an operator nothing and is undebuggable.
 */
function rethrow(error) {
  throw error;
}

export default {
  /**
   * Claim the next batch of sites to review. The claim expires server-side,
   * so abandoning the tab returns the batch to the pool on its own.
   */
  queue(limit) {
    return Vue.axios.get(`${MODULE_ROUTE}/queue`, { params: { limit } }).catch(rethrow);
  },
  /** One already-accepted site, for redoing its logo outside the queue. */
  item(id) {
    return Vue.axios.get(`${MODULE_ROUTE}/${id}/item`).catch(rethrow);
  },
  stats() {
    return Vue.axios.get(`${MODULE_ROUTE}/stats`).catch(rethrow);
  },
  /** Logos created per editor, and how fast. Admin only. */
  editorStats() {
    return Vue.axios.get(`${MODULE_ROUTE}/editorStats`).catch(rethrow);
  },
  /**
   * Harvested logo candidates for one site. Slow on first call for a given
   * site (it crawls), cached afterwards unless `refresh` is set.
   */
  candidates(id, refresh = false) {
    return Vue.axios
      .get(`${MODULE_ROUTE}/${id}/candidates`, { params: { refresh: refresh ? 1 : 0 } })
      .catch(rethrow);
  },
  accept(id, formData) {
    return Vue.axios.post(`${MODULE_ROUTE}/${id}/accept`, formData).catch(rethrow);
  },
  /** Set the site's icon to its own domain rendered as text. */
  applyTextIcon(id) {
    return Vue.axios.post(`${MODULE_ROUTE}/${id}/text-icon`).catch(rethrow);
  },
  skip(id) {
    return Vue.axios.post(`${MODULE_ROUTE}/${id}/skip`).catch(rethrow);
  },
  flag(id, reason) {
    return Vue.axios.post(`${MODULE_ROUTE}/${id}/flag/${reason}`).catch(rethrow);
  },
};
